import { useTranslation } from "react-i18next";
import Teleprompter from "./Telepromter";
import Presentation from "./Presentation";
import { useState, useRef, useEffect } from "react";
import TeleprompterSettings from "./Teleprompter/TeleprompterSettings";
import PresentationSettings from "./Presentation/PresentationSettings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear, faPlay, faPause, faRotateLeft, faAlignLeft, faDisplay } from "@fortawesome/free-solid-svg-icons";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";

// The labels are too long for the control bar on a phone, so below the md
// breakpoint .ctrl-btn-label is visually hidden (assets/css/Custom.css) and the
// icon carries the button on its own. The text stays in the DOM as the button's
// accessible name.
const CtrlBtn = ({ onClick, icon, label, active, danger, inverted }) => (
  <button
    onClick={onClick}
    title={label}
    className="ctrl-btn"
    style={{
      display: "inline-flex", alignItems: "center", gap: 7,
      padding: "9px 18px", borderRadius: 10, border: "none", cursor: "pointer",
      fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 13,
      background: danger
        ? "rgba(243,78,78,0.12)"
        : active
        ? "#038edc"
        : inverted ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)",
      color: danger ? "#f34e4e" : active ? "#fff" : inverted ? "#111827" : "rgba(255,255,255,0.85)",
      transition: "background 0.15s ease, color 0.15s ease",
    }}
    onMouseEnter={(e) => {
      if (!active && !danger) e.currentTarget.style.background = inverted ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.15)";
    }}
    onMouseLeave={(e) => {
      if (!active && !danger) e.currentTarget.style.background = inverted ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)";
    }}
  >
    <FontAwesomeIcon icon={icon} />
    <span className="ctrl-btn-label">{label}</span>
  </button>
);

const TabBtn = ({ active, onClick, icon, label, inverted }) => (
  <button
    onClick={onClick}
    style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "10px 24px", borderRadius: 10, border: "none", cursor: "pointer",
      fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 13,
      background: active ? (inverted ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.12)") : "transparent",
      color: active ? (inverted ? "#111827" : "#fff") : (inverted ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.5)"),
      transition: "all 0.15s ease",
      borderBottom: active ? "2px solid #038edc" : "2px solid transparent",
    }}
  >
    <FontAwesomeIcon icon={icon} />
    {label}
  </button>
);

const TelepromterWrapper = ({ text }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("text");
  const [speed, setSpeed] = useState(40);
  const [marginTop, setMarginTop] = useState(0);
  const [marginBottom, setMarginBottom] = useState(75);
  const [paddingLeft, setPaddingLeft] = useState(1);
  const [paddingRight, setPaddingRight] = useState(10);
  const [fontSize, setFontSize] = useState(35);
  const [countdown, setCountdown] = useState(5);
  const [startMode, setStartMode] = useState("countdown");
  const [mirrorHorizontal, setMirrorHorizontal] = useState(false);
  const [mirrorVertical, setMirrorVertical] = useState(false);
  const [invertColors, setInvertColors] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [presentationFontSize, setPresentationFontSize] = useState(48);
  const [sentenceDuration, setSentenceDuration] = useState(5);
  const [presentationMirrorHorizontal, setPresentationMirrorHorizontal] = useState(false);
  const [presentationCountdown, setPresentationCountdown] = useState(5);
  const [presentationStartMode, setPresentationStartMode] = useState("countdown");
  const [presentationMarginTop, setPresentationMarginTop] = useState(0);
  const [presentationMarginBottom, setPresentationMarginBottom] = useState(75);
  const [presentationShowNextSentence, setPresentationShowNextSentence] = useState(false);
  const [showPresentationSettings, setShowPresentationSettings] = useState(false);

  const teleRef = useRef(null);
  const presentationRef = useRef(null);
  const settingsLoaded = useRef(false);
  const teleprompterSaveTimer = useRef(null);
  const presentationSaveTimer = useRef(null);

  useEffect(() => {
    const loadSettings = async () => {
      const [tpResponse, presResponse] = await Promise.all([
        crudApi.justGet(SocialEndpoints.settings.teleprompter),
        crudApi.justGet(SocialEndpoints.settings.presentation),
      ]);

      if (tpResponse?.ok && tpResponse.data) {
        try {
          const s = JSON.parse(tpResponse.data);
          if (s.speed !== undefined) setSpeed(s.speed);
          if (s.marginTop !== undefined) setMarginTop(s.marginTop);
          if (s.marginBottom !== undefined) setMarginBottom(s.marginBottom);
          if (s.paddingLeft !== undefined) setPaddingLeft(s.paddingLeft);
          if (s.paddingRight !== undefined) setPaddingRight(s.paddingRight);
          if (s.fontSize !== undefined) setFontSize(s.fontSize);
          if (s.countdown !== undefined) setCountdown(s.countdown);
          if (s.startMode !== undefined) setStartMode(s.startMode);
          if (s.mirrorHorizontal !== undefined) setMirrorHorizontal(s.mirrorHorizontal);
          if (s.mirrorVertical !== undefined) setMirrorVertical(s.mirrorVertical);
          if (s.invertColors !== undefined) setInvertColors(s.invertColors);
        } catch (_) {}
      }

      if (presResponse?.ok && presResponse.data) {
        try {
          const s = JSON.parse(presResponse.data);
          if (s.fontSize !== undefined) setPresentationFontSize(s.fontSize);
          if (s.sentenceDuration !== undefined) setSentenceDuration(s.sentenceDuration);
          if (s.mirrorHorizontal !== undefined) setPresentationMirrorHorizontal(s.mirrorHorizontal);
          if (s.countdown !== undefined) setPresentationCountdown(s.countdown);
          if (s.startMode !== undefined) setPresentationStartMode(s.startMode);
          if (s.marginTop !== undefined) setPresentationMarginTop(s.marginTop);
          if (s.marginBottom !== undefined) setPresentationMarginBottom(s.marginBottom);
          if (s.showNextSentence !== undefined) setPresentationShowNextSentence(s.showNextSentence);
        } catch (_) {}
      }

      settingsLoaded.current = true;
    };

    loadSettings();
  }, []);

  useEffect(() => {
    if (!settingsLoaded.current) return;
    clearTimeout(teleprompterSaveTimer.current);
    teleprompterSaveTimer.current = setTimeout(() => {
      crudApi.edit(SocialEndpoints.settings.teleprompter, {
        teleprompterSettings: JSON.stringify({ speed, marginTop, marginBottom, paddingLeft, paddingRight, fontSize, countdown, startMode, mirrorHorizontal, mirrorVertical, invertColors }),
      }, true);
    }, 500);
  }, [speed, marginTop, marginBottom, paddingLeft, paddingRight, fontSize, countdown, startMode, mirrorHorizontal, mirrorVertical, invertColors]);

  useEffect(() => {
    if (!settingsLoaded.current) return;
    clearTimeout(presentationSaveTimer.current);
    presentationSaveTimer.current = setTimeout(() => {
      crudApi.edit(SocialEndpoints.settings.presentation, {
        presentationSettings: JSON.stringify({ fontSize: presentationFontSize, sentenceDuration, mirrorHorizontal: presentationMirrorHorizontal, countdown: presentationCountdown, startMode: presentationStartMode, marginTop: presentationMarginTop, marginBottom: presentationMarginBottom, showNextSentence: presentationShowNextSentence }),
      }, true);
    }, 500);
  }, [presentationFontSize, sentenceDuration, presentationMirrorHorizontal, presentationCountdown, presentationStartMode, presentationMarginTop, presentationMarginBottom, presentationShowNextSentence]);

  const isText = activeTab === "text";
  const currentRef = isText ? teleRef : presentationRef;
  const showingSettings = isText ? showSettings : showPresentationSettings;
  const toggleSettings = () => isText
    ? setShowSettings((s) => !s)
    : setShowPresentationSettings((s) => !s);

  // While the settings panel is open the prompter is not mounted, so its ref is
  // null and start() would be a no-op. Close the panel first and fire the start
  // once the prompter is back on screen.
  const startPending = useRef(false);

  const handleStart = () => {
    if (showingSettings) {
      isText ? setShowSettings(false) : setShowPresentationSettings(false);
      startPending.current = true;
      return;
    }
    currentRef.current?.start();
  };

  useEffect(() => {
    if (showingSettings || !startPending.current) return;
    startPending.current = false;
    currentRef.current?.start();
  }, [showingSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ display: "flex", flexDirection: "column", background: invertColors ? "#ffffff" : "#0d1117", borderRadius: 16, overflow: "hidden", minHeight: 480, transition: "background 0.2s ease", border: invertColors ? "1.5px solid #e2e5ea" : "none" }}>

      {/* ── Top bar: tabs + controls ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderBottom: `1px solid ${invertColors ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.07)"}`, gap: 12, flexWrap: "wrap" }}>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4 }}>
          <TabBtn
            active={isText}
            onClick={() => { setActiveTab("text"); setShowSettings(false); }}
            icon={faAlignLeft}
            label={t("Backend.Post.Tabs.Text")}
            inverted={invertColors}
          />
          <TabBtn
            active={!isText}
            onClick={() => { setActiveTab("presentation"); setShowPresentationSettings(false); }}
            icon={faDisplay}
            label={t("Backend.Post.Tabs.Presentation")}
            inverted={invertColors}
          />
        </div>

        {/* Controls */}
        <div className="prompter-controls" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <CtrlBtn onClick={handleStart} icon={faPlay} label="Fullscreen" inverted={invertColors} />
          <CtrlBtn onClick={() => currentRef.current?.toggle()} icon={faPause} label="Pause / Play" inverted={invertColors} />
          <CtrlBtn onClick={() => currentRef.current?.restart()} icon={faRotateLeft} label="Restart" inverted={invertColors} />
          <CtrlBtn
            onClick={toggleSettings}
            icon={faGear}
            label="Settings"
            active={showingSettings}
            inverted={invertColors}
          />
        </div>
      </div>

      {/* ── Content area ── */}
      <div style={{ flex: 1, padding: "24px 28px" }}>
        {isText ? (
          showSettings ? (
            <TeleprompterSettings
              setSpeed={setSpeed}
              speed={speed}
              setMarginBottom={setMarginBottom}
              setMarginTop={setMarginTop}
              setPaddingLeft={setPaddingLeft}
              setPaddingRight={setPaddingRight}
              setFontSize={setFontSize}
              setCountdown={setCountdown}
              countdown={countdown}
              startMode={startMode}
              setStartMode={setStartMode}
              setMirrorHorizontal={setMirrorHorizontal}
              setMirrorVertical={setMirrorVertical}
              setInvertColors={setInvertColors}
              mirrorHorizontal={mirrorHorizontal}
              mirrorVertical={mirrorVertical}
              invertColors={invertColors}
              inverted={invertColors}
              fontSize={fontSize}
              paddingLeft={paddingLeft}
              paddingRight={paddingRight}
            />
          ) : text ? (
            <Teleprompter
              ref={teleRef}
              text={text}
              speed={speed}
              initialMarginTop={marginTop}
              bottomMargin={marginBottom}
              fontSize={fontSize}
              countdownInit={countdown}
              startMode={startMode}
              mirrorVertical={mirrorVertical}
              mirrorHorizontal={mirrorHorizontal}
              invertColors={invertColors}
              paddingRight={paddingRight}
              paddingLeft={paddingLeft}
            />
          ) : null
        ) : (
          showPresentationSettings ? (
            <PresentationSettings
              sentenceDuration={sentenceDuration}
              setSentenceDuration={setSentenceDuration}
              fontSize={presentationFontSize}
              setFontSize={setPresentationFontSize}
              mirrorHorizontal={presentationMirrorHorizontal}
              setMirrorHorizontal={setPresentationMirrorHorizontal}
              countdown={presentationCountdown}
              setCountdown={setPresentationCountdown}
              startMode={presentationStartMode}
              setStartMode={setPresentationStartMode}
              marginTop={presentationMarginTop}
              setMarginTop={setPresentationMarginTop}
              marginBottom={presentationMarginBottom}
              setMarginBottom={setPresentationMarginBottom}
              showNextSentence={presentationShowNextSentence}
              setShowNextSentence={setPresentationShowNextSentence}
              inverted={invertColors}
            />
          ) : text ? (
            <Presentation
              ref={presentationRef}
              text={text}
              fontSize={presentationFontSize}
              sentenceDuration={sentenceDuration}
              mirrorHorizontal={presentationMirrorHorizontal}
              countdownInit={presentationCountdown}
              startMode={presentationStartMode}
              initialMarginTop={presentationMarginTop}
              bottomMargin={presentationMarginBottom}
              showNextSentence={presentationShowNextSentence}
            />
          ) : null
        )}
      </div>
    </div>
  );
};

export default TelepromterWrapper;
